/**
 * Utility functions
 */

import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Combines class names with Tailwind merge
 * Ensures Tailwind classes don't conflict
 */
export function cn(...inputs: ClassValue[])
{
  return twMerge(clsx(inputs));
}

/**
 * Format date to readable string
 */
export function formatDate(date: Date | string): string
{
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format date to relative time (e.g., "2 hours ago")
 */
export function formatRelativeTime(date: Date | string): string
{
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
  if (diffMins < 10080) return `${Math.floor(diffMins / 1440)}d ago`;

  return formatDate(d);
}

/**
 * Truncate text to specified length
 */
export function truncate(text: string, length: number): string
{
  if (text.length <= length) return text;
  return text.slice(0, length) + '...';
}

/**
 * Sanitize a string so it can be used as an Excel worksheet name.
 * Excel rejects the characters * ? : \ / [ ], names longer than 31 chars,
 * names wrapped in apostrophes, empty names and the reserved name "History".
 */
export function sanitizeSheetName(name: string | null | undefined, fallback = 'Sheet1'): string
{
  let safe = (name ?? '')
    .replace(/[*?:\\/[\]]/g, ' ') // forbidden characters
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^'+|'+$/g, '') // cannot start or end with an apostrophe
    .trim();

  if (safe.length > 31) safe = safe.substring(0, 31).trim();
  if (!safe || safe.toLowerCase() === 'history') safe = fallback;

  return safe;
}

/**
 * UTF-8 byte length of a string
 */
export function byteLength(str: string): number
{
  return new Blob([str]).size;
}
